import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import FlatIcon, { FlatIconOptions } from 'src/components/ui/FlatIcon';
import { Creators as CreatorsUsers } from 'src/store/ducks/user';
import { Autocomplete } from 'src/components';
import { useAuth } from 'src/hooks';
import { useSnackbar } from 'notistack';

export default function SelectUserDialog({ title, description, open, loading, onClose, onSelect }) {
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const { loading: users_loading, users, paginate } = useSelector((state) => state.user);

  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => {
    if (!open || users_loading) return;

    dispatch([
      CreatorsUsers.userLoading(true),
      CreatorsUsers.findAllUsers({ query: '', noPagination: true, ...paginate }),
    ]);

    return () => {
      setSelectedUser({ id: null, name: null });
    };
  }, [open, dispatch]);

  useEffect(() => {
    if (!open) return;

    if (!user?.email?.endsWith('@weesu.com.br')) {
      const filteredUsers = users.filter((item) => !item?.name?.includes('.core.'));
      const isDifferent = users.length !== filteredUsers.length;

      if (isDifferent) {
        dispatch(CreatorsUsers.usersSetData(filteredUsers));
      }
    }
  }, [open, user, users, dispatch]);

  if (!open) return;

  const handleSaveUser = () => {
    if (!selectedUser || !selectedUser?.id || !selectedUser?.name) {
      enqueueSnackbar(`Selecione um usuário válido.`, {
        variant: 'error',
      });
      return;
    }

    onSelect({ user: { id: selectedUser?.id, name: selectedUser?.name } });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <Tooltip
        title='Fechar'
        sx={(theme) => ({
          position: 'absolute',
          right: 8,
          top: 8,
          color: theme.palette.grey[500],
        })}>
        <IconButton
          aria-label='fechar'
          onClick={onClose}>
          <FlatIcon
            icon={FlatIconOptions.close}
            marginRight='0'
          />
        </IconButton>
      </Tooltip>

      <DialogContent dividers>
        <Typography sx={{ marginBottom: 1 }}>{description}</Typography>
        <Autocomplete
          label='Usuário'
          labelId='user-label'
          value={selectedUser?.name}
          onChange={(name) => {
            const selected = users?.find((u) => u.name === name);

            setSelectedUser(selected);
          }}
          data={users || []}
          field='name'
        />
      </DialogContent>

      <DialogActions>
        <Button
          onClick={onClose}
          color='error'
          variant='outlined'
          disabled={loading}>
          <FlatIcon icon={FlatIconOptions.abort} /> Cancelar
        </Button>
        <Button
          onClick={handleSaveUser}
          variant='contained'
          color='success'
          startIcon={
            loading ?
              <CircularProgress
                size={16}
                color='inherit'
              />
            : <FlatIcon icon={FlatIconOptions.confirm} />
          }
          disabled={loading}>
          Confirmar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
